import { Middleware } from '@reduxjs/toolkit';
import { CustomContractCommandDto } from 'api/pricing';
import { RootState } from 'app/store';
import { updateContractPrice } from '../../pricing-values/thunks/updateContractPrice';
import { selectContractFunnelById } from '../selectors';
import { mapModifiedFees } from '../utils/customFees';

// Map pour stocker les timeouts par contractFunnelId + contractIdentifier
const priceUpdateTimeouts = new Map<string, NodeJS.Timeout>();

// Fonction helper pour créer une clé unique
const createTimeoutKey = (contractFunnelId: string, contractIdentifier?: string): string => {
  return `${contractFunnelId}${contractIdentifier ? `_${contractIdentifier}` : ''}`;
};

// Configuration du délai (peut être externalisée)
const PRICE_UPDATE_DELAY = 600; // millisecondes

export const priceUpdateMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);

  // Vérifier si l'action est une de celles qui doivent déclencher une mise à jour de prix
  if (
    action.type === 'pricing/updateContractFunnelGuarantees' ||
    action.type === 'pricing/updateContractFunnelItem'
  ) {
    const { contractFunnelId, contractIdentifier } = action.payload;
    const timeoutKey = createTimeoutKey(contractFunnelId, contractIdentifier);

    // Annuler le timeout précédent s'il existe
    const existingTimeout = priceUpdateTimeouts.get(timeoutKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Programmer la nouvelle mise à jour de prix
    const newTimeout = setTimeout(() => {
      try {
        const state = store.getState() as RootState;
        const selector = selectContractFunnelById(contractFunnelId, contractIdentifier);
        const { contractFunnel } = selector(state);
        const riskId = state.pricing.riskId

        if (!contractFunnel || !riskId) {
          console.warn(`ContractFunnel with id ${contractFunnelId} not found`);
          priceUpdateTimeouts.delete(timeoutKey);
          return;
        }

        // Construire le DTO pour la mise à jour
        const customContractCommandDto: CustomContractCommandDto = {
          riskId,
          tunnelId: contractFunnel.id,
          isIndividual: !contractFunnel.contractIdentifier,
          associatedContractId: contractFunnel.contractIdentifier,
          guarantees: contractFunnel.guarantees?.map(guarantee => ({
            guaranteeId: guarantee.guaranteeTunnelId,
            ceiling: guarantee.ceiling ? {
              idVariableValue: guarantee.ceiling.idVariableValue,
              value: guarantee.ceiling.value,
            } : undefined,
            deductible: guarantee.deductible ? {
              idVariableValue: guarantee.deductible.idVariableValue,
              value: guarantee.deductible.value,
            } : undefined,
          })),
          fees: mapModifiedFees(contractFunnel.fees, state.pricing.tunnelFeeValues),
          variables: contractFunnel.variables?.map(variable => ({
            idVariableValue: variable.variableValueId,
            value: variable.value
          }))
        };

        // Dispatcher le thunk de mise à jour de prix
        store.dispatch(updateContractPrice({
          contractFunnelId: contractFunnel.id,
          contractIdentifier: contractFunnel.contractIdentifier,
          customContractCommandDto
        }) as any);

        // Nettoyer le timeout de la map
        priceUpdateTimeouts.delete(timeoutKey);
      } catch (error) {
        console.error('Erreur lors de la mise à jour automatique du prix:', error);
        priceUpdateTimeouts.delete(timeoutKey);
      }
    }, PRICE_UPDATE_DELAY);

    // Stocker le nouveau timeout
    priceUpdateTimeouts.set(timeoutKey, newTimeout);
  }

  return result;
};

// Optionnel : fonction de nettoyage pour les tests ou le développement
export const clearAllPriceUpdateTimeouts = (): void => {
  priceUpdateTimeouts.forEach(timeout => clearTimeout(timeout));
  priceUpdateTimeouts.clear();
};
